import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { FieldBaseClasses, FieldOpenClasses } from '@/components/ui/fieldStyles';
import { formatDisplayDate, getDaysInMonth, getFirstWeekdayOfMonth, getTodayIsoDate, MonthNames, parseIsoDate, toIsoDate } from '@/lib/dates';
import { cn } from '@/lib/utils';

const MonthShortNames = MonthNames.map((monthName) => monthName.slice(0, 3));
const WeekdayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const YearsPerPage = 16;
const YearsBeforeCurrent = 4;
const CalendarViews = { days: 'days', months: 'months', years: 'years' };
const IconButtonClasses =
  'flex size-9 items-center justify-center rounded-md text-steel transition-colors hover:bg-pad-light hover:text-graphite focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-graphite disabled:pointer-events-none disabled:opacity-35';
const HeaderToggleClasses =
  'rounded-md px-2.5 py-1 text-sm font-bold text-graphite transition-colors hover:bg-pad-light focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-graphite';
const GridOptionClasses =
  'rounded-md px-2 py-2 text-sm font-medium tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-graphite disabled:pointer-events-none disabled:text-steel/40';
const DayButtonClasses =
  'flex aspect-square w-full items-center justify-center rounded-md text-sm tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-graphite disabled:pointer-events-none disabled:text-steel/35';

function isMonthBeforeMinimum(year, monthIndex, minimumDate) {
  if (!minimumDate) return false;
  return year < minimumDate.year || (year === minimumDate.year && monthIndex < minimumDate.monthIndex);
}

function getDayStateClasses(isSelected, isToday) {
  if (isSelected) return 'bg-graphite font-bold text-paper';
  if (isToday) return 'bg-pad font-bold text-graphite hover:bg-grid';
  return 'font-medium text-graphite hover:bg-pad-light';
}

function getGridOptionStateClasses(isSelected) {
  return isSelected ? 'bg-graphite font-bold text-paper' : 'text-graphite hover:bg-paper';
}

export function DatePicker({ id, value, onChange, onClose, placeholder = 'Select a date', disablePastDates = false, className, ...triggerProps }) {
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeView, setActiveView] = useState(CalendarViews.days);
  const [viewYear, setViewYear] = useState(0);
  const [viewMonth, setViewMonth] = useState(0);
  const [yearPageStart, setYearPageStart] = useState(0);
  const [todayIso, setTodayIso] = useState('');
  const panelId = `${id}-calendar`;
  const minimumDate = disablePastDates ? parseIsoDate(todayIso) : null;
  const minimumIso = minimumDate ? todayIso : '';

  function closeCalendar(shouldReturnFocus) {
    setIsOpen(false);
    onClose?.();
    if (shouldReturnFocus) triggerRef.current?.focus();
  }

  const dismissCalendar = useEffectEvent(closeCalendar);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) dismissCalendar(false);
    }

    function handleKeyDown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      dismissCalendar(true);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  function showMonth(year, monthIndex) {
    setViewYear(year);
    setViewMonth(monthIndex);
    setActiveView(CalendarViews.days);
  }

  function openCalendar() {
    const currentTodayIso = getTodayIsoDate();
    const anchorDate = parseIsoDate(value) ?? parseIsoDate(currentTodayIso);
    setTodayIso(currentTodayIso);
    setYearPageStart(anchorDate.year - YearsBeforeCurrent);
    showMonth(anchorDate.year, anchorDate.monthIndex);
    setIsOpen(true);
  }

  function handleTriggerClick() {
    if (isOpen) {
      closeCalendar(false);
    } else {
      openCalendar();
    }
  }

  function showPreviousMonth() {
    if (viewMonth === 0) {
      showMonth(viewYear - 1, 11);
    } else {
      showMonth(viewYear, viewMonth - 1);
    }
  }

  function showNextMonth() {
    if (viewMonth === 11) {
      showMonth(viewYear + 1, 0);
    } else {
      showMonth(viewYear, viewMonth + 1);
    }
  }

  function toggleView(view) {
    if (view === CalendarViews.years) setYearPageStart(viewYear - YearsBeforeCurrent);
    setActiveView((currentView) => (currentView === view ? CalendarViews.days : view));
  }

  function selectYear(year) {
    setViewYear(year);
    setActiveView(CalendarViews.days);
  }

  function selectDay(day) {
    onChange(toIsoDate(viewYear, viewMonth, day));
    closeCalendar(true);
  }

  function showToday() {
    const today = parseIsoDate(todayIso);
    showMonth(today.year, today.monthIndex);
  }

  function clearDate() {
    onChange('');
    triggerRef.current?.focus();
  }

  const isPreviousMonthDisabled = isMonthBeforeMinimum(viewMonth === 0 ? viewYear - 1 : viewYear, viewMonth === 0 ? 11 : viewMonth - 1, minimumDate);
  const isPreviousYearPageDisabled = Boolean(minimumDate) && yearPageStart <= minimumDate.year;
  const yearPageYears = isOpen ? Array.from({ length: YearsPerPage }, (_, yearOffset) => yearPageStart + yearOffset) : [];
  const leadingBlankDays = isOpen ? Array.from({ length: getFirstWeekdayOfMonth(viewYear, viewMonth) }, (_, blankIndex) => blankIndex) : [];
  const monthDays = isOpen ? Array.from({ length: getDaysInMonth(viewYear, viewMonth) }, (_, dayIndex) => dayIndex + 1) : [];

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={handleTriggerClick}
        className={cn(FieldBaseClasses, 'flex h-12 items-center gap-3 px-4 text-left', value && 'pr-12', isOpen && FieldOpenClasses, className)}
        {...triggerProps}
      >
        <Calendar aria-hidden="true" className="size-5 shrink-0 text-steel" />
        {value ? (
          <span className="truncate font-medium">{formatDisplayDate(value)}</span>
        ) : (
          <span className="truncate text-steel/80">{placeholder}</span>
        )}
      </button>
      {value ? (
        <button
          type="button"
          aria-label="Clear date"
          onClick={clearDate}
          className="absolute right-2 top-2 flex size-8 items-center justify-center rounded-md text-steel transition-colors hover:bg-pad-light hover:text-graphite focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-graphite"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      ) : null}
      {isOpen ? (
        <div
          id={panelId}
          role="dialog"
          aria-label="Choose a date"
          className="absolute left-0 top-full z-40 mt-2 w-full max-w-80 rounded-lg border border-grid bg-paper p-3 shadow-[0_24px_48px_-24px_rgb(43_48_44/0.45)] sm:p-4"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              aria-label="Previous month"
              onClick={showPreviousMonth}
              disabled={activeView !== CalendarViews.days || isPreviousMonthDisabled}
              className={IconButtonClasses}
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </button>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-pressed={activeView === CalendarViews.months}
                onClick={() => toggleView(CalendarViews.months)}
                className={cn(HeaderToggleClasses, activeView === CalendarViews.months && 'bg-pad')}
              >
                {MonthNames[viewMonth]}
              </button>
              <button
                type="button"
                aria-pressed={activeView === CalendarViews.years}
                onClick={() => toggleView(CalendarViews.years)}
                className={cn(HeaderToggleClasses, 'tabular-nums', activeView === CalendarViews.years && 'bg-pad')}
              >
                {viewYear}
              </button>
            </div>
            <button
              type="button"
              aria-label="Next month"
              onClick={showNextMonth}
              disabled={activeView !== CalendarViews.days}
              className={IconButtonClasses}
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </button>
          </div>

          {activeView === CalendarViews.months ? (
            <div className="grid grid-cols-3 gap-1.5 rounded-lg bg-pad-light p-2">
              {MonthShortNames.map((shortName, monthIndex) => (
                <button
                  key={shortName}
                  type="button"
                  aria-label={`${MonthNames[monthIndex]} ${viewYear}`}
                  aria-pressed={monthIndex === viewMonth}
                  disabled={isMonthBeforeMinimum(viewYear, monthIndex, minimumDate)}
                  onClick={() => showMonth(viewYear, monthIndex)}
                  className={cn(GridOptionClasses, getGridOptionStateClasses(monthIndex === viewMonth))}
                >
                  {shortName}
                </button>
              ))}
            </div>
          ) : null}

          {activeView === CalendarViews.years ? (
            <div className="rounded-lg bg-pad-light p-2">
              <div className="mb-2 flex items-center justify-between">
                <button
                  type="button"
                  aria-label="Previous years"
                  onClick={() => setYearPageStart((currentStart) => currentStart - YearsPerPage)}
                  disabled={isPreviousYearPageDisabled}
                  className={IconButtonClasses}
                >
                  <ChevronLeft aria-hidden="true" className="size-4" />
                </button>
                <span className="text-sm font-semibold tabular-nums text-steel">
                  {yearPageYears[0]} to {yearPageYears[yearPageYears.length - 1]}
                </span>
                <button
                  type="button"
                  aria-label="Next years"
                  onClick={() => setYearPageStart((currentStart) => currentStart + YearsPerPage)}
                  className={IconButtonClasses}
                >
                  <ChevronRight aria-hidden="true" className="size-4" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {yearPageYears.map((year) => (
                  <button
                    key={year}
                    type="button"
                    aria-pressed={year === viewYear}
                    disabled={Boolean(minimumDate) && year < minimumDate.year}
                    onClick={() => selectYear(year)}
                    className={cn(GridOptionClasses, getGridOptionStateClasses(year === viewYear))}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {activeView === CalendarViews.days ? (
            <>
              <div aria-hidden="true" className="mb-1 grid grid-cols-7 gap-0.5">
                {WeekdayHeaders.map((weekday) => (
                  <span key={weekday} className="py-1 text-center text-xs font-semibold text-steel">
                    {weekday}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {leadingBlankDays.map((blankIndex) => (
                  <span key={`blank-${blankIndex}`} aria-hidden="true" />
                ))}
                {monthDays.map((day) => {
                  const isoDate = toIsoDate(viewYear, viewMonth, day);
                  const isToday = isoDate === todayIso;
                  return (
                    <button
                      key={isoDate}
                      type="button"
                      aria-label={formatDisplayDate(isoDate)}
                      aria-pressed={isoDate === value}
                      aria-current={isToday ? 'date' : undefined}
                      disabled={minimumIso !== '' && isoDate < minimumIso}
                      onClick={() => selectDay(day)}
                      className={cn(DayButtonClasses, getDayStateClasses(isoDate === value, isToday))}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          <div className="mt-3 flex items-center justify-between gap-3 border-t border-grid pt-3">
            <button
              type="button"
              onClick={showToday}
              className="rounded-sm text-sm font-semibold text-graphite underline decoration-grid decoration-2 underline-offset-4 transition-colors hover:decoration-graphite focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-graphite"
            >
              Today
            </button>
            {value ? <span className="truncate text-sm text-steel">{formatDisplayDate(value)}</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}